import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "vpe_install_dismissed_until";
const DISMISS_DAYS = 14;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
}

function dismissedRecently() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch (e) {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2500);
    }

    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 864e5));
    } catch (e) {
      /* ignore */
    }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      /* ignore */
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] md:bottom-4 md:left-auto md:right-4 md:inset-x-auto pointer-events-none">
      <div className="pointer-events-auto mx-auto md:mx-0 max-w-md rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-white/10 p-4 flex items-start gap-3">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-[#01648d] flex items-center justify-center">
          <Download className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">VPE Portal installieren</p>
          {iosHint ? (
            <p className="text-xs text-slate-300 mt-1 leading-snug">
              Tippe auf <Share className="inline w-3.5 h-3.5 -mt-0.5" /> „Teilen" und dann auf
              „Zum Home-Bildschirm".
            </p>
          ) : (
            <p className="text-xs text-slate-300 mt-1 leading-snug">
              Für schnellen Zugriff direkt vom Startbildschirm – ohne Browser.
            </p>
          )}
          {!iosHint && (
            <button
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#01648d] hover:bg-[#02557a] active:bg-[#014a6b] px-4 py-2 text-sm font-semibold transition-colors"
            >
              <Download className="w-4 h-4" strokeWidth={2} />
              Installieren
            </button>
          )}
        </div>
        <button onClick={dismiss} aria-label="Schliessen" className="shrink-0 text-slate-400 hover:text-white p-1 -m-1">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
